import { useState } from "preact/hooks";
import { HostMark } from "../chrome/host-mark.js";
import { newestFirst, type ViewPhoto } from "../photos.js";
import { COVER_ID, seedSrc } from "../seed.js";
import type { Audience, DemoBrand } from "../types.js";
import { demoQuoteHref } from "../whatsapp.js";

export const FREE_FIRST_EVENT_OFFER = false;

export const FREE_FIRST_EVENT_NOTE = "O primeiro evento de até 10 convidados é grátis.";

export type ShareResult = "shared" | "copied" | "failed";

export async function shareLink(
  url: string,
  title: string,
  text: string,
  nav: Partial<Pick<Navigator, "share" | "clipboard">> = navigator,
): Promise<ShareResult> {
  if (typeof nav.share === "function") {
    try {
      await nav.share({ title, text, url });
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return "shared";
      }
    }
  }
  try {
    await nav.clipboard?.writeText(url);
    return nav.clipboard ? "copied" : "failed";
  } catch {
    return "failed";
  }
}

export interface FinishScreenProps {
  readonly brand: DemoBrand;
  readonly logoUrl: string | null;
  readonly accent: string;
  readonly photos: readonly ViewPhoto[];
  readonly featured: ViewPhoto | null;
  readonly whatsapp: string;
  readonly audience?: Audience;
  readonly shareUrl: string;
  onExplore(): void;
  onRestart(): void;
}

export function FinishScreen({
  brand,
  logoUrl,
  accent,
  photos,
  featured,
  whatsapp,
  audience = "planner",
  shareUrl,
  onExplore,
  onRestart,
}: FinishScreenProps) {
  const [shared, setShared] = useState<ShareResult | null>(null);
  const highlight = featured ?? newestFirst(photos)[0] ?? null;
  const grid = newestFirst(photos).slice(0, 6);
  const client = audience === "client";
  return (
    <div class="d-screen d-scroll" data-theme="light" data-testid="screen-finish">
      <main class="d-finish">
        <p class="d-micro">{client ? `${brand.name} apresenta` : "O que o seu cliente recebe"}</p>
        <h1 class="d-finish-title">
          {client ? (
            <>
              Assim fica <em>o seu evento.</em>
            </>
          ) : (
            <>
              Seu evento, <em>com a sua marca.</em>
            </>
          )}
        </h1>
        <div class="d-recap">
          <figure class="d-recap-item">
            <div class="d-recap-invite" data-theme="dark">
              <img src={seedSrc(COVER_ID, "s")} alt="" />
              <div class="d-recap-invite-body">
                <span class="d-invite-host" style={{ borderBottomColor: accent }}>
                  Convidado por {brand.name}
                </span>
                <strong>{brand.eventName}</strong>
              </div>
            </div>
            <figcaption>Convite</figcaption>
          </figure>
          <figure class="d-recap-item">
            <div class="d-recap-wall" data-theme="dark">
              <div class="d-recap-wall-head">
                <HostMark name={brand.name} logoUrl={logoUrl} />
                <span class="d-live">ao vivo</span>
              </div>
              {highlight ? <img src={highlight.large} alt="" /> : null}
              {highlight ? (
                <span class="d-recap-wall-author">
                  <span>{highlight.author}</span>
                </span>
              ) : null}
            </div>
            <figcaption>Telão</figcaption>
          </figure>
          <figure class="d-recap-item">
            <div class="d-recap-album" data-theme="dark">
              {grid.map((photo) => (
                <img key={photo.id} src={photo.small} alt="" />
              ))}
            </div>
            <figcaption>Álbum</figcaption>
          </figure>
        </div>
        {client ? (
          <div class="d-finish-actions">
            <p class="d-finish-note">Gostou? Fale com {brand.name} para usar no seu evento.</p>
            <button type="button" class="d-button d-secondary d-large" onClick={onRestart}>
              Ver de novo
            </button>
          </div>
        ) : (
          <div class="d-finish-actions">
            <a
              class="d-button d-primary d-large"
              href={demoQuoteHref(brand.name || null)}
              data-testid="finish-quote"
            >
              Pedir orçamento
            </a>
            {FREE_FIRST_EVENT_OFFER ? <p class="d-caption">{FREE_FIRST_EVENT_NOTE}</p> : null}
            <div class="d-finish-pair">
              <button
                type="button"
                class="d-button d-secondary d-large"
                data-testid="share-client"
                onClick={() => {
                  void shareLink(
                    shareUrl,
                    `${brand.eventName} · ${brand.name}`,
                    `Veja como fica o seu evento com ${brand.name}.`,
                  ).then(setShared);
                }}
              >
                Mandar para um cliente
              </button>
              <a class="d-button d-secondary d-large" href={whatsapp}>
                Falar no WhatsApp
              </a>
            </div>
            <p class="d-caption" data-testid="share-note" aria-live="polite">
              {shared === "copied"
                ? "Link copiado. É só colar na conversa com o seu cliente."
                : shared === "failed"
                  ? `Não deu para copiar. O link é: ${shareUrl}`
                  : `Seu cliente vê o convite, a câmera, o telão e o álbum com o nome e a cor de ${brand.name}.${brand.logo ? " O logotipo fica só neste navegador." : ""}`}
            </p>
            <button type="button" class="d-button d-ghost d-large" onClick={onExplore}>
              Explorar à vontade
            </button>
            <button type="button" class="d-link" onClick={onRestart}>
              Recomeçar
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
